import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Search, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useAppStore } from '../../app/appStore';
import { useAuthStore } from '../../features/auth/authStore';
import {
  fetchSocialFollowers,
  fetchSocialFollowing,
  followUser,
  unfollowUser,
  type SocialFollowRow,
} from '../../features/leaderboard/api';
import { userName } from '../../entities/user/types';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import { fmtInt } from '../../shared/lib/format';
import { resolveAvatarUrl } from '../../shared/profile/avatarDefaults';

type FollowsTab = 'followers' | 'following';

/**
 * Following & followers — the Instagram social-list idiom in Sorteia's skin:
 * your @name as the title, two underline tabs (followers first), a light
 * search, flat hairline rows with 44px avatars and an inline follow button.
 */
export function FollowsPage() {
  const t = useI18nStore((state) => state.t);
  useI18nStore((state) => state.language);
  const token = useAuthStore((state) => state.token);
  const user = useAuthStore((state) => state.user);
  const setRoute = useAppStore((state) => state.setRoute);
  const setActiveProfileUserId = useAppStore((state) => state.setActiveProfileUserId);
  const setProfileReturn = useAppStore((state) => state.setProfileReturn);
  const followsTargetTab = useAppStore((state) => state.followsTargetTab);
  const setFollowsTargetTab = useAppStore((state) => state.setFollowsTargetTab);
  const [tab, setTab] = useState<FollowsTab>(followsTargetTab || 'followers');
  const [search, setSearch] = useState('');
  const queryClient = useQueryClient();

  // Opened from a specific count in the Me header → land on that tab, then
  // release the target so a later plain visit starts at the default.
  useEffect(() => {
    if (!followsTargetTab) return;
    setTab(followsTargetTab);
    setFollowsTargetTab(null);
  }, [followsTargetTab, setFollowsTargetTab]);

  const followingQuery = useQuery({
    queryKey: ['social-following', token],
    queryFn: () => fetchSocialFollowing(token || ''),
    enabled: !!token,
  });
  const followersQuery = useQuery({
    queryKey: ['social-followers', token],
    queryFn: () => fetchSocialFollowers(token || ''),
    enabled: !!token,
  });

  const followingRows = followingQuery.data?.items || [];
  const followersRows = followersQuery.data?.items || [];
  const followingIds = useMemo(
    () => new Set(followingRows.map((row) => String(row.user?.id ?? '')).filter(Boolean)),
    [followingRows],
  );

  const activeQuery = tab === 'followers' ? followersQuery : followingQuery;
  const rows = tab === 'followers' ? followersRows : followingRows;
  const needle = search.trim().toLowerCase();
  const filtered = needle
    ? rows.filter((row) => String(row.user?.username || '').toLowerCase().includes(needle))
    : rows;

  const toggleMutation = useMutation({
    mutationFn: ({ id, follow }: { id: string; follow: boolean }) =>
      (follow ? followUser(token || '', id) : unfollowUser(token || '', id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social-following'] });
      queryClient.invalidateQueries({ queryKey: ['social-followers'] });
      queryClient.invalidateQueries({ queryKey: ['public-profile'] });
    },
  });

  function openProfile(id: string) {
    // Coming back from the profile lands here, not on the leaderboard.
    setProfileReturn({ view: null, resultId: null, origin: { route: 'me.follows', roomId: null } });
    setActiveProfileUserId(id);
    setRoute('leaderboard');
  }

  return (
    <section className="tab-page follows-page">
      <header className="tab-page-header">
        <button className="icon-button" type="button" onClick={() => setRoute('me')} aria-label={t('close')}>
          <ArrowLeft size={20} aria-hidden="true" />
        </button>
        <div>
          <h1>@{userName(user)}</h1>
        </div>
      </header>

      <nav className="follows-tabs" role="tablist" aria-label={t('meFollowsEntry')}>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'followers'}
          className={tab === 'followers' ? 'active' : ''}
          onClick={() => setTab('followers')}
        >
          {fmtInt(followersRows.length)} {followersRows.length === 1 ? t('followsFollowerOne') : t('followsFollowersTab')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'following'}
          className={tab === 'following' ? 'active' : ''}
          onClick={() => setTab('following')}
        >
          {fmtInt(followingRows.length)} {t('followsFollowingTab')}
        </button>
      </nav>

      <label className="follows-search">
        <Search size={16} aria-hidden="true" />
        <input
          value={search}
          inputMode="search"
          aria-label={t('followsSearch')}
          placeholder={t('followsSearch')}
          onChange={(event) => setSearch(event.target.value)}
        />
        {search ? (
          <button type="button" onClick={() => setSearch('')} aria-label={t('clear')}>
            <X size={14} aria-hidden="true" />
          </button>
        ) : null}
      </label>

      <div className="follows-list">
        {activeQuery.isLoading ? <div className="follows-empty">{t('loading')}</div> : null}
        {activeQuery.isError ? (
          <div className="follows-empty">
            {t('roomLoadError')}
            <button type="button" className="follows-retry" onClick={() => activeQuery.refetch()}>{t('retry')}</button>
          </div>
        ) : null}
        {!activeQuery.isLoading && !activeQuery.isError && filtered.length === 0 ? (
          <div className="follows-empty">
            {needle
              ? t('followsEmptySearch')
              : tab === 'followers' ? t('followsEmptyFollowers') : t('followsEmptyFollowing')}
          </div>
        ) : null}
        {filtered.map((row) => (
          <FollowRow
            key={`${tab}:${row.user?.id}`}
            row={row}
            isFollowing={tab === 'following' ? true : Boolean(row.followingBack) || followingIds.has(String(row.user?.id ?? ''))}
            followBackLabel={tab === 'followers'}
            pending={toggleMutation.isPending}
            onOpen={openProfile}
            onToggle={(id, follow) => toggleMutation.mutate({ id, follow })}
          />
        ))}
      </div>
    </section>
  );
}

function FollowRow({ row, isFollowing, followBackLabel, pending, onOpen, onToggle }: {
  row: SocialFollowRow;
  isFollowing: boolean;
  followBackLabel: boolean;
  pending: boolean;
  onOpen: (id: string) => void;
  onToggle: (id: string, follow: boolean) => void;
}) {
  const t = useI18nStore((state) => state.t);
  const [broken, setBroken] = useState(false);
  const id = String(row.user?.id ?? '');
  const username = String(row.user?.username || '').trim();
  const vip = Number(row.user?.vipLevel || 0);
  const url = broken ? '' : resolveAvatarUrl({
    avatar_thumb: row.user?.avatarThumbUrl,
    avatar_default_id: row.user?.avatarDefaultId ?? undefined,
  });
  return (
    <div className="follow-row">
      <button type="button" className="follow-row-main" onClick={() => onOpen(id)} disabled={!id}>
        <span className="follow-avatar" aria-hidden="true">
          {url
            ? <img src={url} alt="" loading="lazy" decoding="async" width={44} height={44} onError={() => setBroken(true)} />
            : (username.slice(0, 1).toUpperCase() || '·')}
        </span>
        <span className="follow-names">
          <strong>@{username || t('raffleUnknownPlayer')}</strong>
          {row.online
            ? <small className="follow-live"><em className="leaderboard-live-dot" aria-hidden="true" />{t('leaderboardPlayingNow')}</small>
            : vip > 0 ? <small>VIP {fmtInt(vip)}</small> : null}
        </span>
      </button>
      {/* Follow/unfollow is the only action here. This slot used to become a
          "copy their bet" button whenever the person was at a table with a live
          pick, which is the one thing the row must not offer: the draw is
          verifiably random, so their pick is not a tip. Their profile is. */}
      <button
        type="button"
        className={`profile-follow-btn is-compact ${isFollowing ? 'following' : ''}`}
        disabled={pending || !id}
        aria-pressed={isFollowing}
        onClick={() => onToggle(id, !isFollowing)}
      >
        {isFollowing ? t('profileFollowing') : followBackLabel ? t('followsFollowBack') : t('profileFollow')}
      </button>
    </div>
  );
}
